<template>
  <div class="place-create-form">
    <div v-if="showFullModal" class="zone-name-input-form">
      <RfTextInput
        v-model.trim="name"
        density="compact"
        variant="outlined"
        persistentHint
        autofocus
        placeholder="Name"
        title="Name"
        hint="e.g., New users acquisition"
        :rules="[rules.required]"
        :disabled="userStore.isReadOnly"
      />
      <RfTextInput
        v-model.trim="description"
        density="compact"
        variant="outlined"
        persistentHint
        placeholder="Description"
        title="Description"
        :disabled="userStore.isReadOnly"
        hint="e.g., User acquisition designed around generating new registrations"
      />
      <RfTextInput
        v-model.trim="zone"
        density="compact"
        variant="outlined"
        persistentHint
        autofocus
        placeholder="Name"
        title="Zone Identifier"
        :disabled="userStore.isReadOnly"
        hint="No spaces. Underscore or dash is allowed."
      />
    </div>
    <div class="crmod-promo-type-row">
      <div class="crmod-promo-type">
        <RfSelect
          v-model="placementType"
          title="Zone Type"
          variant="outlined"
          class="grow"
          density="compact"
          itemTitle="label"
          hideDetails
          :items="InlinePromptsArray"
          :disabled="!!model.id || userStore.isReadOnly || lockTypeFields"
        />
      </div>
      <div class="crmod-promo-type">
        <RfSelect
          v-model="deviceType"
          title="Device Type"
          variant="outlined"
          class="grow"
          density="compact"
          hideDetails
          itemTitle="text"
          :items="DeviceTypeWording"
          :disabled="!!model.id || userStore.isReadOnly || lockTypeFields"
          @update:modelValue="v => (v !== 'web' ? (zoneType = 'code') : null)"
        />
      </div>
      <div class="crmod-promo-type">
        <RfSelect
          v-model="selectedDimRatio"
          :items="dimRatioByPathType"
          density="compact"
          variant="outlined"
          class="grow"
          itemTitle="text"
          :disabled="userStore.isReadOnly"
          title="Aspect Ratio"
          hideDetails
        />
      </div>
      <div class="crmod-promo-type">
        <RfSelect
          v-model="zoneType"
          title="Placement Type"
          density="compact"
          variant="outlined"
          class="grow"
          hideDetails
          :items="zoneTypeItems"
          :disabled="zoneTypeDisabled || userStore.isReadOnly"
        />
      </div>
    </div>
    <div class="crmod-promo-dims-row">
      <label>Dimensions</label>
      <div>
        <div class="crmod-promo-type *:grow">
          <RfTextInput
            v-model.trim="settings.width"
            variant="outlined"
            density="compact"
            title="Width"
            class="grow"
            :disabled="currDimValue !== 'custom' || userStore.isReadOnly"
            name="width"
            placeholder="e.g., 200px, 50%"
            persistentHint
            hideDetails
            bgColor="#fff"
          />
        </div>
        <div class="crmod-promo-type *:grow">
          <RfTextInput
            v-model.trim="settings.height"
            variant="outlined"
            density="compact"
            title="Height"
            class="grow"
            :disabled="userStore.isReadOnly"
            name="height"
            placeholder="e.g., 200px, 50%"
            persistentHint
            hideDetails
            bgColor="#fff"
          />
        </div>
      </div>
    </div>
    <div v-if="model.device_type === 'web'" class="crmod-promo-dims-row">
      <label class="text-no-wrap">Mobile Web</label>
      <div>
        <div class="crmod-promo-type *:grow">
          <RfTextInput
            v-model.trim="settings.m_width"
            variant="outlined"
            density="compact"
            :disabled="currDimValue !== 'custom' || userStore.isReadOnly"
            title="Mobile Width"
            name="width"
            placeholder="e.g., 200px, 50%"
            persistentHint
            hideDetails
            bgColor="#fff"
          />
        </div>
        <div class="crmod-promo-type *:grow">
          <RfTextInput
            v-model.trim="settings.m_height"
            variant="outlined"
            density="compact"
            title="Mobile Height"
            :disabled="userStore.isReadOnly"
            name="height"
            placeholder="e.g., 200px, 50%"
            persistentHint
            hideDetails
            bgColor="#fff"
          />
        </div>
      </div>
    </div>
    <div v-if="showFullModal" class="zone-name-input-form">
      <RfSelect
        v-model="displayType"
        title="Appearance"
        density="compact"
        variant="outlined"
        itemTitle="text"
        hideDetails
        :items="appearanceStyles"
        :disabled="userStore.isReadOnly"
      >
        <template #item="{ item, props }">
          <v-list-item v-bind="props" :class="item.raw.class" :title="item.raw.text"> </v-list-item>
        </template>
      </RfSelect>
    </div>
  </div>
</template>

<script>
import {
  DEFAULT_PROMO_DIMENSIONS,
  PROMO_WIDTH_MULTIPLIER,
  getRatioOptionsByPromoType,
  getDefaultPromoRatio,
} from "@/utils/PromoDimUtils";
import RfSelect from "@/components/legacy/RfSelectLegacy.vue";
import RfTextInput from "@/components/legacy/RfInputLegacy.vue";
import { computed, ref } from "vue";
import { DeviceTypeWording } from "@/utils/constants/DevicesConstants";
import { InlinePrompts } from "@/utils/constants/PromoTypesConstants";
import { useUserStore } from "@/pinia/userStore";
import { getAppearanceStyle } from "@/utils/promptGroupUtils";

export default {
  name: "RfZoneId",
  components: { RfTextInput, RfSelect },
  props: {
    model: { type: Object, default: () => ({}) },
    showFullModal: { type: Boolean, default: false },
    lockTypeFields: { type: Boolean, default: false },
  },
  expose() {
    return {
      placementType: this.placementType,
      deviceType: this.deviceType,
      fillModel: this.fillModel,
      isValid: this.isValid,
    };
  },
  setup({ model }) {
    const zone = ref(model.zone);
    const name = ref(model.name);

    return {
      zone,
      name,
      DeviceTypeWording: Object.values(DeviceTypeWording),
      InlinePromptsArray: Object.values(InlinePrompts),
      userStore: useUserStore(),
      isValid: computed(() => !!zone.value && /^[\w-]+$/.test(zone.value) && !!name.value),
    };
  },
  data() {
    return {
      description: this.model.description,
      placementType: this.model.path_type,
      deviceType: this.model.device_type,
      settings: this.model.settings,
      zoneType: this.model.zone_type,
      displayType: this.model.display_type,
      originalZoneType: this.model.id && this.model.zone_type,
      rules: { required: value => !!value || "Name field is required." },
      currDimValue: null,
      zoneTypeItems: [
        { title: "Live", value: "live" },
        { title: "Code", value: "code" },
      ],
    };
  },
  computed: {
    dimRatioByPathType() {
      return getRatioOptionsByPromoType(this.placementType);
    },
    selectedDimRatio: {
      get() {
        return this.settings.iab || getDefaultPromoRatio(this.placementType);
      },
      set(value) {
        this.currDimValue = value;
      },
    },
    zoneTypeDisabled() {
      return this.originalZoneType === "code" || this.deviceType !== DeviceTypeWording.web.value;
    },
    appearanceStyles() {
      return getAppearanceStyle(this.deviceType);
    },
    supportSlider() {
      return this.deviceType === DeviceTypeWording.web.value;
    },
  },
  watch: {
    model(to) {
      this.zone = to.zone;
    },
    settings: {
      handler(to) {
        if (this.currDimValue !== "custom") {
          const number = parseFloat(to.height, 10) || 0;
          const postfix = to.height.replace(`${number}`, "");
          const width = PROMO_WIDTH_MULTIPLIER[this.currDimValue] * number;
          to.width = `${width}${postfix}`;
          const mNumber = parseFloat(to.m_height, 10);
          const mPostfix = to.m_height.replace(`${mNumber}`, "");
          const mWidth = PROMO_WIDTH_MULTIPLIER[this.currDimValue] * mNumber;
          to.m_width = `${mWidth}${mPostfix}`;
        }
      },
      deep: true,
    },
    currDimValue(to) {
      if (to !== this.settings.iab) {
        this.settings = { ...DEFAULT_PROMO_DIMENSIONS[this.placementType][to] };
      }
    },
    placementType(to) {
      if (to && DEFAULT_PROMO_DIMENSIONS[to]) {
        this.settings = { ...DEFAULT_PROMO_DIMENSIONS[to][this.currDimValue] };
      }
      this.setDefaultAppearance();
    },
    deviceType() {
      this.setDefaultAppearance();
    },
  },
  mounted() {
    this.currDimValue = this.selectedDimRatio;

    if (!this.displayType) this.displayType = this.supportSlider ? "slider" : "stack";
  },
  methods: {
    setDefaultAppearance() {
      if (!this.supportSlider && this.displayType === "slider")
        return (this.displayType = this.appearanceStyles[0]);
    },
    fillModel(model) {
      if (!this.name) throw new Error("Name field can't be empty");
      model.name = this.name;
      model.description = this.description;
      model.zone = this.zone;
      model.path_type = this.placementType;
      model.device_type = this.deviceType;
      model.settings = this.settings;
      model.zone_type = this.zoneType;
      model.display_type = this.displayType;
    },
  },
};
</script>
